import { ValidationSchema } from 'fastest-validator';
import validator from '@/utils/validator';
import { SchemaClass } from '@/utils/validator/decorators/decorator.types';
import { FormRules, RuleType, ValidatorRule } from '@/composable/ui/useForm';1

const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const passwordRegex = /^(?=.*[0-9])(?=.*[a-z])(?=.*[A-Z])[0-9a-zA-Z!%&@#$^*?_~,:;\][]{8,32}$/;
const jaRegex = /^[\u30A1-\u30F3\u30FC]+$/;
const jaAllCharactersRegex = /^[\u3041-\u3096\u30A1-\u30FC\u3400-\u4DBF\u4E00-\u9FFF々ゝゞー・﨑ヽヾ]+$/;
const phoneRegex = /^\d{1,5}-\d{1,4}-\d{3,4}$/
const textRegex = /^[\u000A\u000D\u0020-\u007E\u3000-\u303F\u3040-\u309F\u30A0-\u30FF\u3400-\u4DBF\u4E00-\u9FFF\uF900-\uFAFF\uFF01-\uFF5E\uFF61-\uFF9F\uE000-\uE757]+$/;
function detectFullWidth(value: string): boolean {
  if (!value) return false;
  return [...value].some((char) => {
    const code = char.charCodeAt(0);
    return (code >= 0x2000 && code <= 0xff60) || code >= 0xffa0;
  });
}

export const invalidText = '入力に使用できない文字が含まれています。';

export function useValidator() {
  return {
    validate<T>(rules: ValidatorRule<T>, value: T) {
      let error = '';
      if (Array.isArray(rules)) {
        (rules as ((v: T) => string | boolean)[]).forEach((rule) => {
          const res = rule(value);
          if (typeof res === 'string') error = res;
          return error;
        });
      } else {
        const resValue = validator.validate(value, { ...(rules as ValidationSchema), $$root: true });
        if (Array.isArray(resValue)) {
          error = resValue[0].message as string;
        }
      }

      return error;
    },
    validateForm<T extends object>(rules: FormRules<T>, value: T) {
      Object.keys(rules)
        .forEach((key) => {
          const fieldRule = rules[key as keyof FormRules<T>];
          if (Array.isArray(fieldRule)) {
            const fieldRules = fieldRule as RuleType[];
            fieldRules.some((rule) => {
              const res = rule(value[key as keyof T]);
              if (typeof res === 'string') {
                const error = Error();
                error.message = `${String(key)}: ${res}`;
                throw error;
              }
              return false;
            });
          }
        });
    },
    rules: {
      required: (value: string) => !!value || 'この項目は必須です',
      email: (value: string) => emailRegex.test(value) || '「メールアドレス」を正しく入力してください。',
      password: (value: string) => passwordRegex.test(value) || '「パスワード」を正しく入力してください。',
      max(form: { maxLength: number | string, text?: string }) {
        return (value: string) => {
          if (!value) return true;
          return (
            value.toString().length <= parseFloat(form.maxLength as string) || form.text || '入力文字列が長過ぎます'
          );
        };
      },
      passwordWithText(form: { text?: string }) {
        return (value: string) => { return passwordRegex.test(value) || form.text }
      },
      min(form: { minLength: number | string, text?: string }) {
        return (value: string) => {
          if (!value) return true;
          return value.length >= parseFloat(form.minLength as string) || form.text || '項目を正しく入力してください。';
        };
      },
      maxNumber(maxValue: number | string) {
        return (value: number) => {
          if (!value) return true;
          return value <= parseFloat(maxValue as string) || `${maxValue}以下の値を入力してください。`;
        };
      },
      isSame(email: string) {
        return (value: string) => value === email || 'メールアドレスが上記と一致しません';
      },
      minSize(size: number) {
        return (item: Set<any>) => item.size >= size || `物件種別を必ず${size}つ選択してください`;
      },
      isKana: (value: string) => jaRegex.test(value) || invalidText,
      isValidRequiredText: (value: string) => {
        if (!value) return 'この項目は必須です';
        return textRegex.test(value) || invalidText;
      },
      onlyFullWight: (value: string) => detectFullWidth(value) || 'この項目は必須です',
      requiredWithLabelName(form: { name: string, message?: string }) {
        return (value: string | { id: number }) => {
          if (typeof value === 'object' && !value.id) return `「${form.name}」${ form.message ? form.message : 'を入力してください。' }`;
          return (!!value || `「${form.name}」${ form.message ? form.message : 'を入力してください。' }`);
        };
      },
      requiredValidTextWithLabelNameAnd(form: { name: string, message?: string }) {
        return (value: string | { id: number }) => {
          if (typeof value === 'object' && !value.id) return `「${form.name}」${ form.message ? form.message : 'を入力してください。' }`;
          if (!value) return `「${form.name}」${ form.message ? form.message : 'を入力してください。' }`;
          return textRegex.test(<string>value) || invalidText;
        };
      },
      requiredWithLabelNameWithDependency(form: { name: string, message?: string, dependency?: string | number }) {
        return (value: string | { id: number }) => {
          if (!form.dependency) return true;
          if (typeof value === 'object' && !value.id) return `「${form.name}」${ form.message ? form.message : 'を入力してください。' }`;
          return (!!value || `「${form.name}」${ form.message ? form.message : 'を入力してください。' }`);
        };
      },
      onlyJapaneseCharactersWithLabelName(form: { name: string }) {
        //Todo Fix the error text for japanese characters
        return (value: string) => {
          return (jaAllCharactersRegex.test(value) || invalidText);
        };
      },
      isKanaWithlabelName(form: { name: string }) {
        return (value: string) => {
          return (jaRegex.test(value) || `「${form.name}」をカタカナで入力してください。`);
        };
      },
      onlyFullWightWithLabelName(form: { name: string }) {
        return (value: string) => {
          return (detectFullWidth(value) || `「${form.name}」全角で入力してください。`);
        };
      },
      onlyHalfWidthNumbersWithLabelName(form: { name: string }) {
        return (value: string) => {
          return (!detectFullWidth(value) || `「${form.name}」を正しく入力してください。`);
        };
      },
      isPhone(){
        return (value: string) => {
          const minPhoneLength = 12
          const maxPhoneLength = 13
          //value.length >= parseFloat(form.minLength as string) || form.text || '項目を正しく入力してください。'
          if (value.length >= 10 && value.length <= 11 && !value.includes("-")){
            return "「電話番号」はハイフンを入れて入力してください。"
          }
          if (value.length < minPhoneLength ){
            return "「電話番号」を正しく入力してください。"
          }
          if (value.length > maxPhoneLength ){
            return "「電話番号」を正しく入力してください。"
          }
          return (phoneRegex.test(value) || `「電話番号」を正しく入力してください。`);
        };
      },
      decimal(form: { name?: string }) {
        return (value: string) => {
          let result: true | string = true;
          const error = `${form.name ? `「${form.name}」 ` : ''}0.01の倍数を入力してください。`;
          if (value) {
            if ((String(value).match(/\.\w{3}/g) || []).length === 1) result = error;
            if ((String(value).match(/\.\w{0}/g) || []).length === 1 && !(String(value).match(/\.\w{1}/g) || []).length) result = error;
            if ((String(value).match(/\./g) || []).length > 1) result = error;
            if (String(value).startsWith('.')) result = error;
          }
          return result;
        }
      }
    },
  };
}

export function useSchemaValidator<T extends typeof SchemaClass>(schemaClass: T) {
  return (form: unknown) => validator.validate(form, schemaClass.getSchema<T['prototype']>());
}
